import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Check, Phone, Camera, Loader2, HelpCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_SRC = "/assets/brand/dallaslisted-logo.png";
const LOGO_ALT =
  "DallasListed — Find It. Get It. Done. Local Businesses. Real Solutions.";

const BrandLogo = ({ variant, testId }) => (
  <img
    src={LOGO_SRC}
    alt={LOGO_ALT}
    className={`dl-logo dl-logo--${variant}`}
    data-testid={testId}
    draggable="false"
  />
);

const SiteHeader = () => (
  <header className="dl-header" data-testid="site-header">
    <div className="dl-header__inner">
      <a href="/" className="dl-header__brand" data-testid="header-logo-link">
        <BrandLogo variant="header" testId="header-logo" />
      </a>
    </div>
  </header>
);

const TRUST_POINTS = [
  { label: "Local DFW Pro", testId: "trust-point-local-dfw-pro" },
  {
    label: "Commercial & Private Properties",
    testId: "trust-point-commercial-private",
  },
  { label: "Fast Response", testId: "trust-point-fast-response" },
];

const Hero = () => (
  <section className="dl-hero" data-testid="hero-section">
    <div className="dl-hero__inner">
      <p className="dl-hero__eyebrow" data-testid="hero-eyebrow">
        DFW Parking Lot Services
      </p>
      <h1 className="dl-hero__title" data-testid="hero-title">
        Need Parking Lot Striping?
      </h1>
      <p className="dl-hero__subtitle" data-testid="hero-subtitle">
        Get a Fast Quote From a Local DFW Striping Pro.
      </p>
      <p className="dl-hero__body" data-testid="hero-body">
        Tell us what you need and where the property is. You can even upload
        photos to help get a faster, more accurate response.
      </p>
      <a href="#quote" className="dl-cta-primary" data-testid="hero-primary-cta">
        Get My Free Quote
      </a>
      <p className="dl-hero__microcopy" data-testid="hero-microcopy">
        Takes About 60 Seconds &bull; No Obligation
      </p>
      <ul className="dl-trust" data-testid="hero-trust-points">
        {TRUST_POINTS.map((point) => (
          <li
            key={point.testId}
            className="dl-trust__item"
            data-testid={point.testId}
          >
            <Check size={16} strokeWidth={3} aria-hidden="true" />
            <span>{point.label}</span>
          </li>
        ))}
      </ul>
      <div className="dl-call" data-testid="hero-secondary-call">
        <span className="dl-call__label">Already know what you need?</span>
        <a
          href="tel:+14696262466"
          className="dl-call__link"
          data-testid="hero-call-now-link"
        >
          <Phone size={16} aria-hidden="true" />
          Call Now
        </a>
        <span className="dl-call__number" data-testid="hero-call-number">
          (469) 626-2466
        </span>
      </div>
    </div>
  </section>
);

const StickyCta = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`dl-sticky-cta${visible ? " dl-sticky-cta--visible" : ""}`}
      data-testid="sticky-cta"
      aria-hidden={!visible}
    >
      <a
        href="#quote"
        className="dl-cta-primary dl-cta-primary--sticky"
        data-testid="sticky-cta-button"
        tabIndex={visible ? 0 : -1}
      >
        Get Free Quote
      </a>
    </div>
  );
};

const ProofStrip = () => (
  <section className="dl-proof" data-testid="proof-section">
    <div className="dl-proof__inner">
      <h2 className="dl-proof__title" data-testid="proof-title">
        See the Difference Fresh Striping Makes
      </h2>
      <div className="dl-proof__card" data-testid="proof-card">
        <img
          src="/assets/brand/strip-mall-before-after.jpg"
          alt="Strip mall parking lot before line striping and after line striping"
          className="dl-proof__img"
          data-testid="proof-image"
          loading="lazy"
          draggable="false"
        />
      </div>
      <p className="dl-proof__caption" data-testid="proof-caption">
        Fresh lines. Better flow. Stronger first impressions for your property.
      </p>
    </div>
  </section>
);

const SERVICES = [
  {
    label: "Restripe Existing Lot",
    testId: "service-chip-restripe",
    img: "/assets/services/restripe.jpg",
    alt: "Freshly restriped parking lot with crisp stall lines and crosshatching",
  },
  {
    label: "New Parking Lot / Layout",
    testId: "service-chip-new-layout",
    img: "/assets/services/new-layout.jpg",
    alt: "Brand new parking lot layout with fresh white striping",
  },
  {
    label: "ADA / Handicap Striping",
    testId: "service-chip-ada",
    img: "/assets/services/ada.jpg",
    alt: "Blue ADA handicap parking symbols and access aisles",
  },
  {
    label: "Fire Lane Striping",
    testId: "service-chip-fire-lane",
    img: "/assets/services/fire-lane.jpg",
    alt: "Red painted fire lane curb marked no parking",
  },
  { label: "Other / Not Sure", testId: "service-chip-other", icon: true },
];

const ServicesStrip = ({ onSelect }) => {
  const [selected, setSelected] = useState(null);

  const pick = (label) => {
    setSelected(label);
    onSelect({ label, ts: Date.now() });
  };

  return (
    <section className="dl-services" data-testid="services-section">
      <div className="dl-services__inner">
        <h2 className="dl-services__title" data-testid="services-title">
          What Do You Need Done?
        </h2>
        <div className="dl-svc-grid" data-testid="services-chips">
          {SERVICES.map((s) => (
            <a
              key={s.testId}
              href="#quote"
              onClick={() => pick(s.label)}
              className={`dl-svc${selected === s.label ? " dl-svc--selected" : ""}`}
              data-testid={s.testId}
              aria-pressed={selected === s.label}
            >
              <span className="dl-svc__media">
                {s.icon ? (
                  <span className="dl-svc__icon-wrap">
                    <HelpCircle size={40} strokeWidth={1.8} aria-hidden="true" />
                  </span>
                ) : (
                  <img
                    src={s.img}
                    alt={s.alt}
                    className="dl-svc__img"
                    loading="lazy"
                    draggable="false"
                  />
                )}
                {selected === s.label && (
                  <span className="dl-svc__badge" data-testid={`${s.testId}-badge`}>
                    <Check size={14} strokeWidth={3.5} aria-hidden="true" />
                  </span>
                )}
              </span>
              <span className="dl-svc__label">{s.label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const QuoteForm = ({ prefill }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    property_address: "",
    message: "",
  });
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, message: prefill.label }));
    }
  }, [prefill]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onPhotos = (e) => setPhotos(Array.from(e.target.files || []).slice(0, 5));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      photos.forEach((p) => data.append("photos", p));
      await axios.post(`${API}/leads`, data);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section className="dl-quote" id="quote" data-testid="quote-section">
      <div className="dl-quote__inner">
        <h2 className="dl-quote__title" data-testid="quote-title">
          Get My Free Quote
        </h2>
        <p className="dl-quote__subtitle" data-testid="quote-subtitle">
          Tell us about your lot — takes about 60 seconds.
        </p>
        {status === "success" ? (
          <div className="dl-quote__success" data-testid="quote-success-message">
            <Check size={28} strokeWidth={3} aria-hidden="true" />
            <p>
              Request received. A local DFW striping pro will reach out to you
              shortly.
            </p>
          </div>
        ) : (
          <form className="dl-quote__form" onSubmit={onSubmit} data-testid="quote-form">
            <label className="dl-field">
              <span className="dl-field__label">Full Name *</span>
              <input
                type="text"
                name="name"
                required
                value={form.name}
                onChange={onChange}
                className="dl-field__input"
                data-testid="quote-name-input"
                autoComplete="name"
              />
            </label>
            <label className="dl-field">
              <span className="dl-field__label">Phone Number *</span>
              <input
                type="tel"
                name="phone"
                required
                value={form.phone}
                onChange={onChange}
                className="dl-field__input"
                data-testid="quote-phone-input"
                autoComplete="tel"
              />
            </label>
            <label className="dl-field">
              <span className="dl-field__label">Property Address / City</span>
              <input
                type="text"
                name="property_address"
                value={form.property_address}
                onChange={onChange}
                className="dl-field__input"
                data-testid="quote-address-input"
                placeholder="Where is the lot located?"
              />
            </label>
            <label className="dl-field">
              <span className="dl-field__label">What do you need?</span>
              <textarea
                name="message"
                rows="3"
                value={form.message}
                onChange={onChange}
                className="dl-field__input"
                data-testid="quote-message-input"
                placeholder="Restripe existing lot, new layout, handicap spaces, fire lane..."
              />
            </label>
            <label className="dl-photo" data-testid="quote-photos-label">
              <Camera size={20} aria-hidden="true" />
              <span>
                {photos.length > 0
                  ? `${photos.length} photo${photos.length > 1 ? "s" : ""} attached`
                  : "Add photos of your lot (optional, up to 5)"}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onPhotos}
                className="dl-photo__input"
                data-testid="quote-photos-input"
              />
            </label>
            {status === "error" && (
              <p className="dl-quote__error" data-testid="quote-error-message">
                Something went wrong sending your request. Please try again.
              </p>
            )}
            <button
              type="submit"
              className="dl-cta-primary dl-quote__submit"
              data-testid="quote-submit-button"
              disabled={status === "sending"}
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={18} className="dl-spin" aria-hidden="true" />
                  Sending...
                </>
              ) : (
                "Get My Free Quote"
              )}
            </button>
            <p className="dl-quote__fineprint" data-testid="quote-fineprint">
              No obligation. Your info goes only to a local DFW striping pro.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};


const SiteFooter = () => (
  <footer className="dl-footer" data-testid="site-footer">
    <div className="dl-footer__inner">
      <p className="dl-footer__copy" data-testid="footer-copy">
        &copy; {new Date().getFullYear()} DallasListed.com
      </p>
    </div>
  </footer>
);

const Home = () => {
  const [serviceRequest, setServiceRequest] = useState(null);

  return (
    <div className="dl-page" data-testid="home-page">
      <SiteHeader />
      <main data-testid="main-content">
        <Hero />
        <ProofStrip />
        <ServicesStrip onSelect={setServiceRequest} />
        <QuoteForm prefill={serviceRequest} />
      </main>
      <SiteFooter />
      <StickyCta />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
